<template>
	<NuxtLink
		:to="project.cta.link"
		:aria-label="`View ${project.heading} project`"
		class="relative aspect-square w-full h-full group overflow-hidden bg-gray-200 focus:outline-none focus:ring-4 focus:ring-blue-500/50 rounded-sm transition-shadow duration-300"
	>
		<!-- Skeleton shimmer loader -->
		<div
			v-if="!imageLoaded"
			class="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-shimmer z-10"
			style="background-size: 200% 100%"
		></div>

		<NuxtPicture
			v-if="project.image"
			:src="project.image"
			:alt="project.heading"
			:img-attrs="{
				class:
					'w-full h-full object-cover group-hover:scale-110 transition-transform duration-[5000ms] ease-linear',
				loading: index === 0 ? 'eager' : 'lazy',
				fetchpriority: index === 0 ? 'high' : 'auto',
			}"
			:preload="index === 0"
			provider="cloudflare"
			quality="80"
			format="webp"
			sizes="sm:100vw md:50vw lg:50vw"
			placeholder
			@load="handleImageLoad"
		/>

		<!-- Overlay with backdrop blur for depth -->
		<div
			class="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-100 min-[1115px]:opacity-0 min-[1115px]:group-hover:opacity-100 transition-all duration-500 ease-out min-[1115px]:group-hover:backdrop-blur-[2px]"
		></div>

		<!-- Text container with stagger animation -->
		<div
			class="absolute inset-0 flex items-end justify-start p-8 text-white opacity-100 min-[1115px]:opacity-0 min-[1115px]:group-hover:opacity-100 transition-all duration-500 ease-out"
		>
			<div
				class="transform min-[1115px]:translate-y-4 min-[1115px]:group-hover:translate-y-0 transition-transform duration-500 ease-out"
			>
				<h2 class="text-3xl font-bold tracking-tight leading-tight">
					{{ project.heading }}
				</h2>
				<!-- Subtle underline accent -->
				<div
					class="h-1 w-0 bg-white/80 mt-2 min-[1115px]:group-hover:w-16 transition-all duration-700 ease-out"
				></div>
			</div>
		</div>

		<!-- Subtle corner accent - positioned based on grid location -->
		<div
			:class="cornerClasses"
			class="absolute w-0 h-0 min-[1115px]:group-hover:w-12 min-[1115px]:group-hover:h-12 border-white/40 transition-all duration-700 ease-out"
		></div>
	</NuxtLink>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';

const props = defineProps({
	project: {
		type: Object,
		required: true,
	},
	index: {
		type: Number,
		required: true,
	},
});

const imageLoaded = ref(false);

const handleImageLoad = () => {
	imageLoaded.value = true;
};

// Fallback: Hide skeleton after 3 seconds even if load event doesn't fire
onMounted(() => {
	setTimeout(() => {
		if (!imageLoaded.value) {
			imageLoaded.value = true;
		}
	}, 3000);
});

// Position corner accent based on grid position
const cornerClasses = computed(() => {
	const positions = {
		0: 'top-4 left-4 border-t-2 border-l-2', // Top-left box → top-left corner
		1: 'top-4 right-4 border-t-2 border-r-2', // Top-right box → top-right corner
		2: 'bottom-4 left-4 border-b-2 border-l-2', // Bottom-left box → bottom-left corner
		3: 'bottom-4 right-4 border-b-2 border-r-2', // Bottom-right box → bottom-right corner
	};
	return positions[props.index] || positions[0];
});
</script>

<style scoped>
@keyframes shimmer {
	0% {
		background-position: 200% 0;
	}
	100% {
		background-position: -200% 0;
	}
}

.animate-shimmer {
	animation: shimmer 2s infinite linear;
}
</style>
